import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export interface GeneratedLogin {
  role: string;
  name: string;
  email: string;
  /** Only present on create/add-staff responses — never re-sent on a plain list/detail read. */
  password?: string;
}

export interface TestClinicSummary {
  clinicId: string;
  clinicName: string;
  createdAt: string;
  labEnabled: boolean;
  pharmacyEnabled: boolean;
  doctorCount: number;
  staffCount: number;
  patientCount: number;
  generatedLogins?: GeneratedLogin[];
}

export interface CreateTestClinicRequest {
  clinicName?: string;
  doctorCount: number;
  staffCount: number;
  includeLab: boolean;
  includePharmacy: boolean;
  patientCount: number;
  includeActivityTrail: boolean;
  /** Also creates a patient-portal login for each generated patient. */
  includePatientLogins: boolean;
}

export type AddStaffRole = 'doctor' | 'staff' | 'lab_admin' | 'lab_staff' | 'pharmacy_admin' | 'pharmacy_staff';

/** "Fill every feature": data for every module and role in one test clinic. */
export interface FeatureDataRequest {
  /** Patients the clinic should have (topped up to this). */
  patients: number;
  /** Days of history. */
  daysBack: number;
  /** Days of upcoming appointments and bookings. */
  daysAhead: number;
}

export interface FeatureDataArea {
  key: string;
  label: string;
  status: 'DONE' | 'FAILED';
  created: Record<string, number>;
  error?: string | null;
  millis: number;
}

export interface FeatureDataRun {
  id: string;
  clinicId: string;
  clinicName: string;
  status: 'RUNNING' | 'DONE' | 'DONE_WITH_ERRORS' | 'FAILED';
  step: string;
  areas: FeatureDataArea[];
  startedAt: string;
  finishedAt?: string | null;
  error?: string | null;
}

export interface FeatureDataStatus {
  areas: { key: string; label: string }[];
  lastRun?: FeatureDataRun | null;
}

export interface AddStaffRequest {
  role: AddStaffRole;
  count: number;
}

export interface AddPatientsRequest {
  count: number;
  includeActivityTrail: boolean;
  /** Also creates a patient-portal login for each generated patient. */
  includePatientLogins: boolean;
}

/** Demo directory: hundreds of realistic public doctors, clinics, labs, reviews, articles and answers. */
export interface DemoDirectoryRequest {
  doctors: number;
  labSharePercent: number;
  reviews: boolean;
  articles: boolean;
  questions: boolean;
  /** Share of clinics offering video visits (default 50). */
  videoSharePercent?: number;
}

export interface DemoDirectoryRun {
  id: string;
  status: 'RUNNING' | 'DONE' | 'FAILED';
  doctorsTarget: number;
  doctorsCreated: number;
  clinicsCreated: number;
  labClinics: number;
  videoClinics: number;
  reviews: number;
  articles: number;
  questions: number;
  answers: number;
  error?: string | null;
  sampleLogins: GeneratedLogin[];
  startedAt: string;
  finishedAt?: string | null;
}

export interface DemoDirectoryStatus {
  live: { clinics: number; doctors: number; labClinics: number; videoClinics: number };
  lastRun?: DemoDirectoryRun | null;
}

export const testDataModule = {
  /** Test Data Studio — super_admin only, dev/UAT only (404s if disabled for the environment). */
  listTestClinics: () => fetchApi<TestClinicSummary[]>('/api/v1/test-data/clinics'),

  /** One page of the test clinics (demo-directory clinics excluded), newest first, searched by name (medopd-docs 117). */
  listTestClinicsPage: (page: number, size: number, search?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search?.trim()) params.set('search', search.trim());
    return fetchApi<PagedResponse<TestClinicSummary>>(`/api/v1/test-data/clinics?${params.toString()}`);
  },

  getTestClinic: (clinicId: string) =>
    fetchApi<TestClinicSummary>(`/api/v1/test-data/clinics/${clinicId}`),

  createTestClinic: (req: CreateTestClinicRequest) =>
    fetchApi<TestClinicSummary>('/api/v1/test-data/clinics', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  addStaff: (clinicId: string, req: AddStaffRequest) =>
    fetchApi<TestClinicSummary>(`/api/v1/test-data/clinics/${clinicId}/staff`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  addPatients: (clinicId: string, req: AddPatientsRequest) =>
    fetchApi<TestClinicSummary>(`/api/v1/test-data/clinics/${clinicId}/patients`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  featureData: () => fetchApi<FeatureDataStatus>('/api/v1/test-data/feature-data'),

  /** Starts a background run filling one test clinic with data for every feature and role. */
  fillEveryFeature: (clinicId: string, req: FeatureDataRequest) =>
    fetchApi<FeatureDataRun>(`/api/v1/test-data/clinics/${clinicId}/feature-data`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  demoDirectory: () => fetchApi<DemoDirectoryStatus>('/api/v1/test-data/demo-directory'),

  generateDemoDirectory: (req: DemoDirectoryRequest) =>
    fetchApi<DemoDirectoryRun>('/api/v1/test-data/demo-directory', { method: 'POST', body: JSON.stringify(req) }),

  /** Video visits on for a share of the existing demo clinics (default half), off for the rest. */
  applyDemoVideo: (sharePercent: number) =>
    fetchApi<{ videoClinics: number; otherClinics: number; videoDoctors: number }>('/api/v1/test-data/demo-directory/video', {
      method: 'POST', body: JSON.stringify({ sharePercent }),
    }),

  removeDemoDirectory: () =>
    fetchApi<{ clinics: number; doctors: number }>('/api/v1/test-data/demo-directory', { method: 'DELETE' }),

  deactivateTestClinic: (clinicId: string) =>
    fetchApi<{ message: string }>(`/api/v1/test-data/clinics/${clinicId}/deactivate`, {
      method: 'POST',
    }),
};
